import React, {
  JSX,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { GlobalProps, useFormatCurrency } from 'src/utils/globalProps'

import { Container } from '@awaze/design-system.components.container'
import {
  breakpoints,
  useWindowWidth,
  pxToRems,
} from '@awaze/design-system.utils.react'
import { Button } from '@awaze/design-system.components.button'
import { Headline, HeadlineProps } from '@awaze/design-system.components.text'
import { PropertyCard } from '@awaze/design-system.compositions.property-card'
import { IPropertyCard } from '@awaze/design-system.compositions.property-card/dist/property-card.types'
import { useIntl } from 'react-intl'
import { getBaseUrl } from 'src/utils/helpers/helpers'
import styled from 'styled-components'
import { useObserverDataLayer } from 'src/utils/dataLayer/useObserverDatalayer'
import {
  podsPropertyDatalayer,
  podsPropertyTileOnClickDatalayer,
} from 'src/utils/dataLayer/podsPropertyDatalayer'
import { CarouselProps } from '@awaze/design-system.components.carousel'
import { ConditionalWrapper } from '../ConditionalWrapper/ConditionalWrapper'

const PropertyCardCarousel = React.lazy(() =>
  import('@awaze/design-system.compositions.property-card-carousel').then(
    (module) => ({
      default: module.PropertyCardCarousel,
    }),
  ),
)

const StackContainer = styled.div<{
  hasVerticalPadding: boolean
}>`
  min-height: 400px;
  padding: ${(p) => (p.hasVerticalPadding ? '48px 0 48px 24px' : '0 0 0 24px')};

  @media (min-width: ${breakpoints[0]}px) {
    padding-right: 16px;
    padding-bottom: ${(p) => (p.hasVerticalPadding ? 64 : 0)}px;
  }

  @media (min-width: ${breakpoints[1]}px) {
    padding: ${(p) => (p.hasVerticalPadding ? 64 : 0)}px 16px;
  }

  // Add margin-top to any immediate successor of type StackContainer
  & + & {
    margin-top: 60px;
    @media (min-width: ${breakpoints[0]}px) {
      margin-top: 80px;
    }
  }
`

const StyledPropertyCardWrapper = styled.div`
  swiper-container,
  .stacked {
    padding-right: ${pxToRems(24)};
    display: grid;
    grid-row-gap: ${pxToRems(40)};
    grid-column-gap: ${pxToRems(24)};
    margin-left: 0 !important;
    margin-right: 0 !important;

    > article {
      min-width: 0;
    }

    @media (min-width: ${breakpoints[0]}px) {
      padding-right: 0;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (min-width: ${breakpoints[1]}px) {
      padding-right: 0;
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`

const TitleAndBtn = styled.div`
  margin-bottom: 32px;
  padding-right: 24px;

  @media (min-width: ${breakpoints[0]}px) {
    padding-right: 0;
    margin-bottom: 0;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
`

const asHeadlineProps = (
  title?: string | HeadlineProps,
): HeadlineProps & { children: React.ReactNode } => {
  if (typeof title === 'string') {
    return {
      as: 'h3',
      sizes: [200, 300, 300],
      marginBottom: 40,
      hasBorder: false,
      children: title,
    }
  }
  return title
}

interface PropertyStackOrCarouselProps {
  properties: Omit<IPropertyCard, 'translations'>[]
  title?: string | HeadlineProps
  query?: string | undefined
  amountOfCards?: number | undefined
  buttonText?: string | undefined
  hasVerticalSpacing?: boolean
  showAsCarousel?: boolean
}

export const PropertyStackOrCarousel: React.FC<
  PropertyStackOrCarouselProps
> = ({
  title,
  query,
  buttonText,
  properties,
  hasVerticalSpacing,
  amountOfCards,
  showAsCarousel,
}): JSX.Element => {
  const { brand } = useContext(GlobalProps)
  const width = useWindowWidth()
  const isMobile = width < breakpoints[0]
  const pagination: boolean = width >= breakpoints[0]

  const { formatDate, formatMessage } = useIntl()
  const { formatCurrencyToObject } = useFormatCurrency()
  const { elementRef, tms } = useObserverDataLayer(
    podsPropertyDatalayer({ properties, title }),
  )
  const [isClientSideRendering, setIsClientSideRendering] = useState(false)

  useEffect(() => {
    setIsClientSideRendering(true)
  }, [])

  if (brand === 'hoseasons' || brand === 'cottages.com') {
    properties.forEach((property) => {
      delete property.wasPrice
      delete property.startDate
    })
  }

  const memoProperties: IPropertyCard[] = useMemo(
    () =>
      properties
        .slice(0, amountOfCards ?? properties.length)
        .map((property, index) => ({
          ...property,
          onPropertyLinkClick: () => {
            podsPropertyTileOnClickDatalayer({
              tms,
              property,
              productNumberInRank: index + 1,
              title,
            })
          },
          translations: {
            priceUnavailableCTALabel: formatMessage({
              id: 'property-carousel.unavailable-price',
              defaultMessage: 'For price, add a start date',
            }),
            calloutBadge: formatMessage({
              id: 'property-carousel.callout-badge',
              defaultMessage: "Customers' Choice",
            }),
            reviewRating: formatMessage({
              id: 'property-carousel.rating',
              defaultMessage: 'Feefo rating',
            }),
            outOf: formatMessage({
              id: 'property-carousel.out-of',
              defaultMessage: 'out of',
            }),
            stars: formatMessage({
              id: 'property-carousel.stars',
              defaultMessage: 'stars',
            }),
            nights: formatMessage({
              id: 'property-carousel.nights',
              defaultMessage: 'nights',
            }),
            from: formatMessage({
              id: 'property-carousel.from',
              defaultMessage: 'From',
            }),
            linkTo: formatMessage({
              id: 'property-carousel.link-to',
              defaultMessage: 'Link to',
            }),
            propertyLinkLabel: formatMessage({
              id: 'property-carousel.view',
              defaultMessage: 'View',
            }),
            dateFormatter: (date: string) =>
              `${formatDate(date, { day: 'numeric' })} ${formatDate(date, {
                month: 'short',
              })}`,
            reserveTodayForOnly: formatMessage({
              id: 'property-carousel.reserve-today',
              defaultMessage: 'Reserve today for only',
            }),
            includedExtrasLabel: formatMessage({
              id: 'property-carousel.includedExtras',
              defaultMessage: 'Price includes',
            }),
            propertyLinkAriaLabel: formatMessage(
              {
                id: 'property-carousel.link-aria-label',
                defaultMessage: 'view {title} (opens in a new window)',
              },
              { title: property.title },
            ),
            formatCurrency: formatCurrencyToObject,
            urgencyBadge: formatMessage({
              id: 'property-carousel.urgency-badge',
              defaultMessage: "Book before it's gone!",
            }),
            reserveForSelectedProperties: formatMessage({
              id: 'property-carousel.reserve-for-selected-properties',
              defaultMessage: 'on selected properties',
            }),
            propertySpecs: {
              guests: {
                singular: formatMessage({
                  id: 'property-specs.guests.singular',
                  defaultMessage: 'Guest',
                }),
                plural: formatMessage({
                  id: 'property-specs.guests.plural',
                  defaultMessage: 'Guests',
                }),
              },
              studio: formatMessage({
                id: 'property-specs.studio',
                defaultMessage: 'Studio',
              }),
              bedrooms: {
                singular: formatMessage({
                  id: 'property-specs.bedrooms.singular',
                  defaultMessage: 'Bedroom',
                }),
                plural: formatMessage({
                  id: 'property-specs.bedrooms.plural',
                  defaultMessage: 'Bedrooms',
                }),
              },
              bathrooms: {
                singular: formatMessage({
                  id: 'property-specs.bathrooms.singular',
                  defaultMessage: 'Bathroom',
                }),
                plural: formatMessage({
                  id: 'property-specs.bathrooms.plural',
                  defaultMessage: 'Bathrooms',
                }),
              },
              pets: {
                label: '',
                singular: formatMessage({
                  id: 'property-specs.pets.singular',
                  defaultMessage: 'Pet',
                }),
                plural: formatMessage({
                  id: 'property-specs.pets.plural',
                  defaultMessage: 'Pets',
                }),
                allowed: '',
              },
            },
          },
        })),
    [properties],
  )

  if (!memoProperties || memoProperties.length === 0) {
    return null
  }

  const carouselProps = {
    children: <span />,
    loop: false,
    hideNavButtons: isMobile || !isClientSideRendering,
    swiperOptions: {
      ...{
        slidesPerView: 1.3,
        slidesPerGroup: 1,
        spaceBetween: 24,
        ...(width >= breakpoints[0] && {
          slidesPerView: 3,
          slidesPerGroup: 3,
        }),
        ...(width >= breakpoints[1] && {
          slidesPerView: 4,
          slidesPerGroup: 4,
        }),
      },
      keyboard: false,
      controller: {
        // Unique controller ID to control carousels individually.
        control: btoa(query).slice(0, -2),
      },
    },
    ...(pagination && {
      customPagination: {
        type: 'bullets',
        dynamicBullets: true,
        dynamicMainBullets: 1,
      },
    }),
    translations: {
      previousNavigation: formatMessage({
        id: 'property-carousel.previousNavigation',
        defaultMessage: 'Previous slide',
      }),
      nextNavigation: formatMessage({
        id: 'property-carousel.nextNavigation',
        defaultMessage: 'Next slide',
      }),
    },
  } satisfies CarouselProps

  const stacked = (
    <StyledPropertyCardWrapper>
      <div className="stacked">
        {memoProperties.map((property) => {
          return <PropertyCard key={property.code} {...property} />
        })}
      </div>
    </StyledPropertyCardWrapper>
  )

  return (
    <StackContainer hasVerticalPadding={hasVerticalSpacing}>
      <Container>
        <TitleAndBtn>
          <div>
            <Headline {...asHeadlineProps(title)} />
          </div>
          <div>
            <Button
              as={'a'}
              /**
               * TODO: Temporary solution for not having to change hundred of Contentful Destination-Pages
               * When Contentful Carousel URL's are fixed, remove the replace() from query object:
               * this will replace any query for rating above 5 with a 4-5 rating
               */
              href={`${getBaseUrl(brand)}/search${query?.replace(
                /rating=([5-9]|[1-9][0-9]+)-([6-9]|[1-9][0-9]+)/g,
                'rating=4-5',
              )}`}
              text={buttonText}
              variant="solid"
              colourScheme="brand-primary"
              isFullWidth={isMobile}
              rel="nofollow"
            />
          </div>
        </TitleAndBtn>
        <div ref={elementRef}>
          {showAsCarousel ? (
            <ConditionalWrapper
              condition={!isClientSideRendering}
              Wrapper={StyledPropertyCardWrapper}
            >
              <Suspense fallback={stacked}>
                <PropertyCardCarousel
                  carouselProps={carouselProps}
                  propertyCardsData={memoProperties}
                />
              </Suspense>
            </ConditionalWrapper>
          ) : (
            stacked
          )}
        </div>
      </Container>
    </StackContainer>
  )
}
